import { getSolscanTxLink } from '@/utils'
import { msg } from '@lingui/core/macro'
import { Trans, useLingui } from '@lingui/react/macro'
import clsx from 'clsx'
import { ExternalLink } from 'lucide-react'
import type { CryptoStatus, PayoutStatus } from '../api/models'

const STEPS: { label: ReturnType<typeof msg>; matches: CryptoStatus[] }[] = [
  { label: msg`Preparing`, matches: ['CLAIMING', 'BROADCASTING'] },
  { label: msg`Awaiting confirmation`, matches: ['SUBMITTED'] },
  { label: msg`Delivered`, matches: ['CONFIRMED'] }
]

type PayoutInFlightExpandProps = {
  payoutStatus: PayoutStatus
  cryptoStatus?: CryptoStatus
  txHash?: string | null
}

export const PayoutInFlightExpand = ({ payoutStatus, cryptoStatus, txHash }: PayoutInFlightExpandProps) => {
  const { t } = useLingui()
  const isFailed = payoutStatus === 'FAILED'
  const activeIndex = cryptoStatus ? STEPS.findIndex((step) => step.matches.includes(cryptoStatus)) : -1

  return (
    <div className="flex items-center justify-between gap-3 border-t border-gray-100 pt-3">
      {isFailed ? (
        <div className="text-body-3 text-gray-90">
          <Trans>Payout failed</Trans> —{' '}
          <span className="font-medium">
            <Trans>funds returned to your balance</Trans>
          </span>
          .
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          {STEPS.map((step, index) => {
            const isCurrent = index === activeIndex
            const isPast = activeIndex > -1 && index < activeIndex
            return (
              <div key={step.label.id} className="flex items-center gap-2">
                <span
                  className={clsx(
                    'text-body-3 rounded-full px-2.5 py-1',
                    isCurrent && 'bg-green/10 text-green font-semibold',
                    isPast && 'text-gray-90 bg-gray-100/50',
                    !isCurrent && !isPast && 'text-gray-90/60 bg-gray-100/30'
                  )}
                >
                  {t(step.label)}
                </span>
                {index < STEPS.length - 1 && <span className="text-gray-90/40">→</span>}
              </div>
            )
          })}
        </div>
      )}

      {txHash && (
        <a
          className="text-body-2 text-gray-90 block flex items-center gap-1 hover:underline"
          href={getSolscanTxLink(txHash)}
          target="_blank"
          rel="noopener noreferrer"
        >
          <Trans>View on Solana explorer</Trans>
          <ExternalLink size={14} />
        </a>
      )}
    </div>
  )
}
